import React, { useEffect, useRef, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import { useTranslation } from 'react-i18next';
import Card from '@/components/Card/Card';
import CardHeader from '@/components/Card/CardHeader';
import CardBody from '@/components/Card/CardBody';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
} from '@mui/material';
import DatePicker from 'react-datepicker';
import SearchIcon from '@mui/icons-material/Search';
import EditRounded from '@mui/icons-material/EditRounded';
import VisibilityRounded from '@mui/icons-material/VisibilityRounded';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CustomPaymentTypeSelect from '@/components/CustomPaymentTypeSelect';
import ReplayIcon from '@mui/icons-material/Replay';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import AmountWithCurrency from '@/components/AmountWithCurrency';
import {
  APPROVAL_STATUS,
  checkOrganizationFeatures,
  ENTITY_TYPE,
  getPaymentTypeTranslation,
  ORGANIZATION_FEATURES,
  REFUND_TYPES,
  REFUND_PATH,
} from '@/utility';
import { useGetRefunds } from '@/api/hooks/refunds/useGetRefunds';
import EditManualRefundModal from './CreateOrEditManualRefund/EditManualRefundModal';
import Button from '@/components/CustomButtons/Button';
import moment from 'moment';
import axios from 'axios';
import { API_BASE_URL } from '@/config';
import { RefundsApi } from '../../api/clients/refunds/refunds.api';
import ConfirmationModal from '@/components/ConfirmationModal';
import DeclineConfirmationModal from '@/components/DeclineConfirmationModal';
import loader from '@/assets/img/loader.svg';
import { BusinessUnitsApi } from '@/api/clients/business-units/business-units.api';
import PayeeCategorySelect from '@/components/PayeeCategorySelect';
import FacilitySelect from '@/components/FacilitySelect';
import { StatusHistoryModal } from '@/views/Clients/FacilityTransaction/StatusHistory/StatusHistoryModal';
import { toast } from 'react-toastify';
import useCurrentUser from '@/hooks/useCurrentUser';

const TABLE_COLUMN_COUNT = 11;

const Approvals = ({ role = 'facility' }) => {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const isCustomPaymentTypesEnabled = posthog?.isFeatureEnabled(
    'new-custom-payment-types-workflow',
  );
  const isBulkApproveDeclineEnabled =
    role === 'admin' ||
    checkOrganizationFeatures(ORGANIZATION_FEATURES.showBulkApproveDecline);
  const tableColSpan = isBulkApproveDeclineEnabled
    ? TABLE_COLUMN_COUNT
    : TABLE_COLUMN_COUNT - 1;
  const listInnerRef = useRef();
  const [selectedRefunds, setSelectedRefunds] = useState([]);
  const [currentInfo, setCurrentInfo] = useState(null);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [
    declineConfirmationModalOpen,
    setDeclineConfirmationModalOpen,
  ] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState(null);
  const [confirmationData, setConfirmationData] = useState(null);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [allowedRefundTypes, setAllowedRefundTypes] = useState([]);
  const [filteredBusinessUnits, setFilteredBusinessUnits] = useState([]);
  const [resubmitId, setResubmitId] = useState(null);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const { loginEmail } = useCurrentUser();

  const {
    payments,
    loading,
    startDate,
    setPartialState,
    endDate,
    payeeCategory,
    approvalStatus,
    search,
    refetchRefunds,
    hasMore,
    offSet,
    sortBy,
    handleSearch,
    selectedBusinessUnit,
    facility,
    customPaymentTypeId,
  } = useGetRefunds({
    refundLocation: REFUND_PATH.APPROVALS,
    defaultValues: {
      approvalStatus: APPROVAL_STATUS.WAITING_FOR_APPROVAL,
    },
  });

  const canEditRefund = (refund) => {
    return (
      refund.approval_status === APPROVAL_STATUS.DECLINED &&
      refund.enable_resubmission_approvals &&
      refund.approval_requested_by === loginEmail
    );
  };

  const getApprovalStatusTranslation = (approvalStatus) => {
    switch (approvalStatus) {
      case APPROVAL_STATUS.WAITING_FOR_APPROVAL:
        return t('approvals.status.waitingForApproval');
      case APPROVAL_STATUS.APPROVED:
        return t('approvals.status.approved');
      case APPROVAL_STATUS.DECLINED:
        return t('approvals.status.declined');
      default:
        return approvalStatus;
    }
  };

  const handleSearchChange = (e) => {
    setPartialState({ search: e.target.value });
    handleSearch(e.target.value);
  };

  const handleScroll = () => {
    if (listInnerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listInnerRef.current;
      if (
        scrollTop + clientHeight >= scrollHeight - 10 &&
        hasMore &&
        !loading
      ) {
        setPartialState({ offSet: offSet + 25 });
      }
    }
  };

  const sortByColumn = (columnName) => {
    setPartialState({
      sortBy: {
        name: columnName,
        order: sortBy.name === columnName ? (sortBy.order === 1 ? -1 : 1) : 1,
      },
    });
  };

  const sortArrow = (columnName) => {
    return columnName === sortBy.name ? (sortBy.order === 1 ? '↓' : '↑') : '';
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const visibleRefundIds = payments
        .filter(
          (payment) =>
            payment.approval_status === APPROVAL_STATUS.WAITING_FOR_APPROVAL,
        )
        .map((payment) => payment.id);
      setSelectedRefunds(visibleRefundIds);
    } else {
      setSelectedRefunds([]);
    }
  };

  const handleSelectRefund = (id) => {
    setSelectedRefunds((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((refundId) => refundId !== id)
        : [...prevSelected, id],
    );
  };

  const isRefundSelected = (id) => selectedRefunds.includes(id);

  const updateApprovalStatus = async (id, status, comment = '') => {
    try {
      const payload = {
        approvalStatus: status,
        comment: status === APPROVAL_STATUS.DECLINED ? comment : undefined,
      };

      await axios.post(API_BASE_URL + `/approve-refund/${id}`, payload);
      refetchRefunds();
    } catch (error) {
      console.error('Failed to update refund status:', error.message);
    }
  };

  const bulkUpdateApprovalStatus = async (status, comment = '') => {
    try {
      const payload = {
        refundIds: selectedRefunds,
        approvalStatus: status,
        comment: status === APPROVAL_STATUS.DECLINED ? comment : undefined,
      };

      await axios.post(API_BASE_URL + '/bulk-approve-refunds', payload);
      setSelectedRefunds([]);
      refetchRefunds();
    } catch (error) {
      setSelectedRefunds([]);
      refetchRefunds();
      console.error('Failed to perform bulk update:', error.message);
    }
  };

  const handleConfirmAction = async () => {
    if (confirmationAction === 'bulk') {
      await bulkUpdateApprovalStatus(confirmationData.status);
    } else if (confirmationAction === 'individual') {
      await updateApprovalStatus(confirmationData.id, confirmationData.status);
    }
    setConfirmationModalOpen(false);
  };

  const handleDeclineConfirmAction = async (comment) => {
    if (confirmationAction === 'bulk') {
      await bulkUpdateApprovalStatus(confirmationData.status, comment);
    } else if (confirmationAction === 'individual') {
      await updateApprovalStatus(
        confirmationData.id,
        confirmationData.status,
        comment,
      );
    }
    setDeclineConfirmationModalOpen(false);
  };

  useEffect(() => {
    const fetchBusinessUnits = async () => {
      try {
        const businessUnits = await BusinessUnitsApi.getBusinessUnitsForForm();
        setBusinessUnits(businessUnits);
        setFilteredBusinessUnits(businessUnits);
      } catch (error) {
        console.error('Error fetching business units:', error);
      }
    };

    const fetchAllowedRefundTypes = async () => {
      try {
        const response = await RefundsApi.getAllowedRefundTypes();
        setAllowedRefundTypes(response.data.allowed_refund_types);
      } catch (error) {
        console.error('Error fetching allowed refund types:', error);
      }
    };

    fetchBusinessUnits();
    fetchAllowedRefundTypes();
  }, []);

  const handleResubmitRefund = async (id) => {
    setResubmitId(id);
  };

  const confirmResubmitRefund = async () => {
    if (!resubmitId) return;
    try {
      await axios.post(`${API_BASE_URL}/approval-resubmit/${resubmitId}`);
      refetchRefunds();
    } catch (error) {
      console.error('Failed to resubmit refund:', error.message);
    } finally {
      setResubmitId(null);
    }
  };

  const fetchStatusHistory = async () => {
    if (!selectedRefund) {
      setStatusHistory([]);
      return;
    }

    try {
      const fetchedStatusHistory = (
        await axios.get(
          API_BASE_URL + `/status_history/approvals/` + selectedRefund,
        )
      ).data;

      if (fetchedStatusHistory.ach_data_backfill_error) {
        toast.error(fetchedStatusHistory.ach_data_backfill_error);
      }

      setStatusHistory(fetchedStatusHistory.history);
    } catch (error) {
      console.error('Error fetching status history:', error);
      toast.error('Failed to fetch status history');
    }
  };

  useEffect(() => {
    fetchStatusHistory();
  }, [selectedRefund]);

  const handleRetryPullingAch = async () => {
    fetchStatusHistory();
  };

  const onFacilitySelect = (facilityId) => {
    setPartialState({ facility: facilityId });
    setFilteredBusinessUnits(
      businessUnits.filter(
        (businessUnit) => businessUnit.facility_id == facilityId,
      ),
    );
  };

  return (
    <Card>
      <CardHeader
        style={{ display: 'flex', justifyContent: 'space-between' }}
        color='primary'>
        <h4>{t('approvals.title')}</h4>
      </CardHeader>
      <CardBody>
        <div>
          <div
            style={{
              display: 'flex',
              gap: '10px',
              flexWrap: 'wrap',
              margin: '20px 0',
            }}>
            <div>
              <div className='custom-search-box'>
                <select
                  id='approval-status'
                  className='full-width-elem'
                  style={{ height: '35px' }}
                  value={approvalStatus}
                  onChange={(e) =>
                    setPartialState({ approvalStatus: e.target.value })
                  }>
                  <option value=''>{t('approvals.filters.all')}</option>
                  <option value={APPROVAL_STATUS.WAITING_FOR_APPROVAL}>
                    {t('approvals.filters.awaitingApproval')}
                  </option>
                  <option value={APPROVAL_STATUS.APPROVED}>
                    {t('approvals.filters.approved')}
                  </option>
                  <option value={APPROVAL_STATUS.DECLINED}>
                    {t('approvals.filters.declined')}
                  </option>
                </select>
                <span className='search-icon'></span>
              </div>
            </div>
            {checkOrganizationFeatures(
              ORGANIZATION_FEATURES.showBusinessUnitsPage,
            ) && (
              <div>
                <div className='custom-search-box'>
                  <select
                    id='business-unit'
                    className='full-width-elem'
                    style={{ height: '35px' }}
                    value={selectedBusinessUnit}
                    onChange={(e) =>
                      setPartialState({ selectedBusinessUnit: e.target.value })
                    }>
                    <option value=''>
                      {t('approvals.filters.allBusinessUnits')}
                    </option>
                    {(filteredBusinessUnits.length
                      ? filteredBusinessUnits
                      : businessUnits
                    )?.map((unit) => (
                      <option key={unit.id} value={unit.id}>
                        {unit.name}
                      </option>
                    ))}
                  </select>
                  <span className='search-icon'></span>
                </div>
              </div>
            )}

            <FacilitySelect
              value={facility || ''}
              onChange={onFacilitySelect}
              placeholderKey='approvals.filters.facilities'
              parentKey='approvals'
              childKey='payment_information'
              permission='read'
            />
            {(role === 'admin' || !isCustomPaymentTypesEnabled) && (
              <PayeeCategorySelect
                role={role}
                allowedRefundTypes={allowedRefundTypes}
                payeeCategory={payeeCategory}
                onChange={(e) =>
                  setPartialState({ payeeCategory: e.target.value })
                }
              />
            )}
            {(role === 'admin' || isCustomPaymentTypesEnabled) && (
              <CustomPaymentTypeSelect
                value={customPaymentTypeId}
                onChange={(e) =>
                  setPartialState({
                    customPaymentTypeId: e.target.value,
                  })
                }
              />
            )}

            <div>
              {t('approvals.dateFilters.from')}&nbsp;&nbsp;
              <DatePicker
                className='full-width-elem-date-picker'
                selected={startDate}
                onChange={(date) => setPartialState({ startDate: date })}
              />
            </div>
            <div>
              {t('approvals.dateFilters.to')}&nbsp;&nbsp;
              <DatePicker
                className='full-width-elem-date-picker'
                selected={endDate}
                onChange={(date) => setPartialState({ endDate: date })}
              />
            </div>
            <div>
              <div className='custom-search-box'>
                <input
                  type='search'
                  className='search-field'
                  style={{ height: '35px' }}
                  value={search}
                  onChange={handleSearchChange}
                  placeholder={t('approvals.search.placeholder')}
                />
                <span className='search-icon'>
                  <SearchIcon />
                </span>
              </div>
            </div>
          </div>
          {isBulkApproveDeclineEnabled && (
            <div
              style={{
                marginBottom: '10px',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px',
              }}>
              <Button
                color='rose'
                disabled={selectedRefunds.length === 0}
                onClick={() => {
                  setConfirmationAction('bulk');
                  setConfirmationData({ status: APPROVAL_STATUS.DECLINED });
                  setDeclineConfirmationModalOpen(true);
                }}>
                {t('approvals.buttons.bulkDecline')}
              </Button>
              <Button
                color='primary'
                disabled={selectedRefunds.length === 0}
                onClick={() => {
                  setConfirmationAction('bulk');
                  setConfirmationData({ status: APPROVAL_STATUS.APPROVED });
                  setConfirmationModalOpen(true);
                }}>
                {t('approvals.buttons.bulkApprove')}
              </Button>
            </div>
          )}
          {loading && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 10,
              }}>
              <img src={loader} alt='Loading...' />
            </div>
          )}
          <div
            style={{
              overflow: 'auto',
              height: '70vh',
              width: '100%',
              position: 'relative',
            }}
            ref={listInnerRef}
            onScroll={handleScroll}>
            <Table width='100%' id='template'>
              <TableHead>
                <TableRow style={{ cursor: 'pointer', userSelect: 'none' }}>
                  {isBulkApproveDeclineEnabled && (
                    <TableCell>
                      <Checkbox
                        checked={
                          payments.length > 0 &&
                          selectedRefunds.length === payments.length
                        }
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                  )}
                  <TableCell>{t('approvals.table.cpdId')}</TableCell>
                  <TableCell onClick={() => sortByColumn('created_at')}>
                    {t('approvals.table.dateCreated')}
                    {sortArrow('created_at')}
                  </TableCell>
                  <TableCell onClick={() => sortByColumn('updated_at')}>
                    {t('approvals.table.lastUpdated')} {sortArrow('updated_at')}
                  </TableCell>
                  <TableCell onClick={() => sortByColumn('last_name')}>
                    {t('approvals.table.customerInfo')} {sortArrow('last_name')}
                  </TableCell>
                  <TableCell onClick={() => sortByColumn('refund_type')}>
                    {t('common.table.paymentCategory')}{' '}
                    {sortArrow('refund_type')}
                  </TableCell>
                  <TableCell onClick={() => sortByColumn('business_unit_name')}>
                    {t('approvals.table.businessUnit')}{' '}
                    {sortArrow('business_unit_name')}
                  </TableCell>
                  <TableCell onClick={() => sortByColumn('facility_name')}>
                    {t('common.facility')} {sortArrow('facility_name')}
                  </TableCell>
                  <TableCell onClick={() => sortByColumn('approval_status')}>
                    {t('approvals.table.approvalStatus')}{' '}
                    {sortArrow('approval_status')}
                  </TableCell>
                  <TableCell onClick={() => sortByColumn('refund_amount')}>
                    {t('approvals.table.amount')} {sortArrow('refund_amount')}
                  </TableCell>
                  <TableCell>{t('approvals.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              {payments && payments.length > 0 ? (
                <TableBody>
                  {payments.map((refund) => (
                    <TableRow key={refund.id}>
                      {isBulkApproveDeclineEnabled && (
                        <TableCell>
                          {refund.approval_status ===
                            APPROVAL_STATUS.WAITING_FOR_APPROVAL && (
                            <Checkbox
                              checked={isRefundSelected(refund.id)}
                              onChange={() => handleSelectRefund(refund.id)}
                            />
                          )}
                        </TableCell>
                      )}
                      <TableCell>{refund.id}</TableCell>
                      <TableCell>
                        {moment(refund.created_at).format('MM/DD/YYYY')}
                        <span style={{ fontSize: '12px' }}>
                          <br />
                          {refund.created_by || 'N/a'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {moment(refund.updated_at).format('MM/DD/YYYY')}
                        <span style={{ fontSize: '12px' }}>
                          <br />
                          {refund.updated_by || 'N/a'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {isCustomPaymentTypesEnabled ? (
                          refund.entity_type === ENTITY_TYPE.INDIVIDUAL ? (
                            <>
                              {refund.first_name} {refund.last_name}{' '}
                            </>
                          ) : (
                            <>{refund.business_payee_name} </>
                          )
                        ) : (
                          <>
                            {refund.first_name} {refund.last_name}{' '}
                          </>
                        )}
                        {canEditRefund(refund) ? (
                          <a
                            title='Edit Customer Information'
                            onClick={() => {
                              setCurrentInfo(refund);
                            }}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              verticalAlign: 'middle',
                            }}>
                            <EditRounded
                              style={{ cursor: 'pointer', fontSize: '18px' }}
                              fontSize='inherit'
                            />
                          </a>
                        ) : (
                          <a
                            title='View Payment Details'
                            onClick={() => {
                              setCurrentInfo(refund);
                            }}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              verticalAlign: 'middle',
                            }}>
                            <VisibilityRounded
                              style={{ cursor: 'pointer', fontSize: '18px' }}
                              fontSize='inherit'
                            />
                          </a>
                        )}
                        {isCustomPaymentTypesEnabled ? (
                          refund.entity_type === ENTITY_TYPE.INDIVIDUAL &&
                          refund.email ? (
                            <span style={{ display: 'flex' }}>
                              <p style={{ fontSize: '12px' }}>
                                ({refund.email})
                                {refund.has_files_attached ? (
                                  <span style={{ fontWeight: 700 }}>
                                    {` - PDFs Attached`}
                                  </span>
                                ) : null}
                              </p>
                            </span>
                          ) : refund.entity_type === ENTITY_TYPE.BUSINESS &&
                            refund.business_payee_name ? (
                            <span style={{ display: 'flex' }}>
                              <p style={{ fontSize: '12px' }}>
                                ({refund.business_payee_name})
                                {refund.has_files_attached ? (
                                  <span style={{ fontWeight: 700 }}>
                                    {` - PDFs Attached`}
                                  </span>
                                ) : null}
                              </p>
                            </span>
                          ) : null
                        ) : !!refund.email ||
                          ((refund.refund_type === REFUND_TYPES.INSURANCE ||
                            refund.refund_type === REFUND_TYPES.BUSINESS) &&
                            refund.business_payee_name) ? (
                          <span style={{ display: 'flex' }}>
                            <p style={{ fontSize: '12px' }}>
                              (
                              {refund.refund_type === REFUND_TYPES.INSURANCE ||
                              refund.refund_type === REFUND_TYPES.BUSINESS
                                ? refund.business_payee_name
                                : refund.email}
                              )
                              {refund.has_files_attached ? (
                                <span style={{ fontWeight: 700 }}>
                                  {` - PDFs Attached`}
                                </span>
                              ) : null}
                            </p>
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {isCustomPaymentTypesEnabled
                          ? refund.custom_payment_type || 'N/A'
                          : getPaymentTypeTranslation(refund.refund_type)}
                      </TableCell>
                      <TableCell>
                        {refund.business_unit_name || 'N/A'}
                      </TableCell>
                      <TableCell>{refund.facility_name || 'N/A'}</TableCell>
                      <TableCell>
                        {getApprovalStatusTranslation(refund.approval_status)}
                      </TableCell>
                      <TableCell>
                        <AmountWithCurrency
                          amount={refund.refund_amount}
                          currency={refund.currency}
                        />
                      </TableCell>
                      <TableCell>
                        <div
                          style={{
                            display: 'flex',
                            gap: '2px',
                            alignItems: 'center',
                          }}>
                          <HistoryRounded
                            style={{ color: '#3AB795', cursor: 'pointer' }}
                            title='Status History'
                            onClick={() => setSelectedRefund(refund.id)}
                          />
                          {refund.approval_status ===
                            APPROVAL_STATUS.WAITING_FOR_APPROVAL && (
                            <>
                              <Button
                                color='rose'
                                style={{
                                  borderRadius: '50%',
                                  width: '32px',
                                  height: '32px',
                                  minWidth: 0,
                                  padding: 0,
                                }}
                                disabled={selectedRefunds.length > 0}
                                onClick={() => {
                                  setConfirmationAction('individual');
                                  setConfirmationData({
                                    id: refund.id,
                                    status: APPROVAL_STATUS.DECLINED,
                                  });
                                  setDeclineConfirmationModalOpen(true);
                                }}>
                                <CloseIcon
                                  color='secondary'
                                  style={{ marginRight: '0px' }}
                                />
                              </Button>
                              <Button
                                color='primary'
                                style={{
                                  borderRadius: '50%',
                                  width: '32px',
                                  height: '32px',
                                  minWidth: 0,
                                  padding: 0,
                                }}
                                disabled={selectedRefunds.length > 0}
                                onClick={() => {
                                  setConfirmationAction('individual');
                                  setConfirmationData({
                                    id: refund.id,
                                    status: APPROVAL_STATUS.APPROVED,
                                  });
                                  setConfirmationModalOpen(true);
                                }}>
                                <CheckIcon
                                  color='secondary'
                                  style={{ marginRight: '0px' }}
                                />
                              </Button>
                            </>
                          )}
                          {canEditRefund(refund) && (
                            <Button
                              color='warning'
                              onClick={() => handleResubmitRefund(refund.id)}
                              title='Resubmit for Approval'>
                              <ReplayIcon color='white' />
                              Resubmit
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              ) : (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={tableColSpan}>
                      {t('approvals.table.noData')}
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </div>
        </div>
        {currentInfo && (
          <EditManualRefundModal
            refundLocation={REFUND_PATH.APPROVALS}
            refund={currentInfo}
            reFetch={refetchRefunds}
            onClose={() => setCurrentInfo(null)}
            role={role}
          />
        )}
        <ConfirmationModal
          open={confirmationModalOpen}
          onClose={() => setConfirmationModalOpen(false)}
          onConfirm={handleConfirmAction}>
          {(() => {
            if (confirmationAction === 'bulk') {
              const count = selectedRefunds.length;
              return `${t(
                'approvals.confirmations.bulkApprovePrefix',
              )} ${count} ${t('approvals.confirmations.bulkApproveSuffix')}`;
            }
            return t('approvals.confirmations.approve');
          })()}
        </ConfirmationModal>
        <DeclineConfirmationModal
          open={declineConfirmationModalOpen}
          onClose={() => setDeclineConfirmationModalOpen(false)}
          onConfirm={handleDeclineConfirmAction}
          isBulk={confirmationAction === 'bulk'}>
          {(() => {
            if (confirmationAction === 'bulk') {
              const count = selectedRefunds.length;
              return `${t(
                'approvals.confirmations.bulkDeclinePrefix',
              )} ${count} ${t('approvals.confirmations.bulkDeclineSuffix')}`;
            }
            return t('approvals.confirmations.decline');
          })()}
        </DeclineConfirmationModal>
        <ConfirmationModal
          open={!!resubmitId}
          onClose={() => setResubmitId(null)}
          onConfirm={confirmResubmitRefund}>
          {t('approvals.confirmations.resubmit')}
        </ConfirmationModal>
        <StatusHistoryModal
          role={role}
          open={selectedRefund !== null}
          statusHistory={statusHistory}
          onRetryPullingAch={handleRetryPullingAch}
          onClose={() => setSelectedRefund(null)}
          refund={payments.find((r) => r.id === selectedRefund)}
        />
      </CardBody>
    </Card>
  );
};

export default Approvals;
